import React, { useMemo, useState } from "react"
import { BlockMath } from "react-katex"
import { calculateTumorVolume, formatNumber } from "../utils/tumorVolume"
import "./VolumeCalculator.css"

export default function VolumeCalculator() {
  const [LD, setLD] = useState("")
  const [SAD, setSAD] = useState("")
  const [submitted, setSubmitted] = useState(null)

  const [hasConfirmedUnits, setHasConfirmedUnits] = useState(false)

  const handleConfirm = () => {
    setHasConfirmedUnits(true)
  }

  const LDNum = Number(LD)
  const SADNum = Number(SAD)

  const LDValid = Number.isFinite(LDNum) && LDNum > 0
  const SADValid = Number.isFinite(SADNum) && SADNum > 0
  const formValid = LDValid && SADValid

  const LDDisplay = LDValid ? LDNum : "LD"
  const SADDisplay = SADValid ? SADNum : "SAD"

  const volume = useMemo(() => {
    if (!submitted) return null
    return calculateTumorVolume(submitted.LD, submitted.SAD)
  }, [submitted])

  const VDisplay =
    submitted != null && volume != null ? formatNumber(volume, 2) : "ETV"

  const eqTeX = `${VDisplay}\\;=\\;4.2\\,\\cdot\\,\\left(\\frac{${LDDisplay} + ${SADDisplay}}{4}\\right)^{3}`

  function sanitizeDecimalInput(raw) {
    if (raw == null) return ""
    let s = String(raw).replace(/,/g, ".")
    s = s.replace(/[^\d.]/g, "")

    const firstDot = s.indexOf(".")
    if (firstDot !== -1) {
      s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, "")
    }
    return s
  }

  function roundTo(value, decimals = 2) {
    const factor = 10 ** decimals
    return Math.round((value + Number.EPSILON) * factor) / factor
  }

  function clampTo2OnBlur(value) {
    if (value === "") return ""
    const n = Number(String(value).replace(",", "."))
    if (!Number.isFinite(n)) return ""
    return roundTo(n, 2).toFixed(2)
  }

  const blockArrowKeys = (e) => {
    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      e.preventDefault()
    }
  }

  const disableWheelChange = (e) => {
    e.currentTarget.blur()
  }

  const hasAnyDigit = /\d/.test(LD) || /\d/.test(SAD)

  const handleLDChange = (e) => {
    const next = sanitizeDecimalInput(e.target.value)
    setLD(next)
    setSubmitted(null)
    setHasConfirmedUnits(false)
  }

  const handleSADChange = (e) => {
    const next = sanitizeDecimalInput(e.target.value)
    setSAD(next)
    setSubmitted(null)
    setHasConfirmedUnits(false)
  }

  const handleCalculate = (e) => {
    e.preventDefault()
    if (!hasConfirmedUnits) return

    if (!formValid) {
      setSubmitted(null)
      return
    }

    const clampedLD = clampTo2OnBlur(LD)
    const clampedSAD = clampTo2OnBlur(SAD)

    setLD(clampedLD)
    setSAD(clampedSAD)
    setSubmitted({ LD: clampedLD, SAD: clampedSAD })
  }

  return (
    <section className="card" aria-label="Tumor volume calculator">
      <span className="guideline">
        Please refer to the applicable trial protocol to ensure measurement guidelines are followed.
      </span>

      <span className="instruction">
        Enter valid values for LD and SAD, confirm inputs and unit of measure, then click Calculate.
      </span>

      <form onSubmit={handleCalculate}>
        <div className="grid">
          <label className="field">
            <span className="label">
              <strong>1. </strong>Enter longest diameter <strong>(cm)</strong>
            </span>

            <div className="input-with-unit">
              <input
                className="measure-input"
                inputMode="decimal"
                type="text"
                pattern="^\d*([.,]\d*)?$"
                value={LD}
                onChange={handleLDChange}
                onBlur={() => setLD(clampTo2OnBlur(LD))}
                onWheel={disableWheelChange}
                onKeyDown={blockArrowKeys}
                placeholder="e.g., 3.52"
              />
              <span className="unit-suffix">
                <strong>cm</strong>
              </span>
            </div>
          </label>

          <label className="field">
            <span className="label">
              <strong>2. </strong>Enter short-axis diameter <strong>(cm)</strong>
            </span>

            <div className="input-with-unit">
              <input
                className="measure-input"
                inputMode="decimal"
                type="text"
                pattern="^\d*([.,]\d*)?$"
                value={SAD}
                onChange={handleSADChange}
                onBlur={() => setSAD(clampTo2OnBlur(SAD))}
                onWheel={disableWheelChange}
                onKeyDown={blockArrowKeys}
                placeholder="e.g., 1.71"
              />
              <span className="unit-suffix">
                <strong>cm</strong>
              </span>
            </div>
          </label>
        </div>

        <div className="equation" aria-live="polite">
          <div className="eq-title">Estimated Tumor Volume Equation</div>
          <div className="eq-line">
            <BlockMath math={eqTeX} />
          </div>
        </div>


        <div className="confirm-banner" role="note" aria-live="polite">
          <div className="confirm-tip-row">
            <strong>3. </strong>
            <button
              type="button"
              className="cta-red-oval"
              onClick={handleConfirm}
              disabled={!hasAnyDigit || hasConfirmedUnits}
              aria-disabled={!hasAnyDigit || hasConfirmedUnits}
            >
              {hasConfirmedUnits ? "Confirmed" : "Confirm"}
            </button>

            <div className="confirm-tip-text">
              Verify input values and confirm the values are in <strong>cm</strong>
            </div>
          </div>
        </div>

        <div className="calculate-button-container">
          <strong>4. </strong>
          <button
            type="submit"
            className="cta-red-oval"
            disabled={!formValid || !hasConfirmedUnits}
            aria-disabled={!formValid || !hasConfirmedUnits}
          >
            Calculate
          </button>

          <div className="inline-result" aria-live="polite">
            {submitted != null && volume != null ? (
              <>
                <span className="inline-result-label">Estimated Tumor Volume:</span>
                <span className="inline-result-value">
                  {formatNumber(volume, 2)}
                  <span className="unit"> mL (cm³ = mL)</span>
                </span>
              </>
            ) : (
              <span className="inline-result-placeholder" />
            )}
          </div>
        </div>
      </form>
    </section>
  )
}